import { useTranslation } from "react-i18next";

import Heading from "@components/layout/Heading";
import Label from "@components/form/Label";
import RadioInput from "@components/form/RadioInput";
import { usePermanentTransferContext } from "@context/PermanentTransferContext";
import Legend from "@components/form/Legend";
import SelectBillingGroup from "@components/form/SelectBillingGroup";

type EnterDetailsProps = {
  onNext: () => void;
  onPrev: () => void;
  onCancel: () => void;
};

const EnterDetails: React.FunctionComponent<EnterDetailsProps> = ({
  onNext,
  onPrev,
  onCancel,
}) => {
  const { t } = useTranslation();
  const {
    level1Resource,
    selectedExtractionRights,
    usageIn,
    setUsageIn,
    changeBillingGroup,
  } = usePermanentTransferContext();

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onNext();
  };

  const isAllBillingGroupsSelected = selectedExtractionRights.every(
    right => right.billingGroup?.id,
  );

  return (
    <form
      className="flex flex-col p-6 grow justify-between"
      onSubmit={handleSubmit}
    >
      <div className="flex flex-col grow gap-6">
        <Heading light>
          {t("approval.permanent_trades.enter_details.title")}
        </Heading>
        <fieldset className="space-y-6">
          <div>
            <Label>
              {t("approval.seasonal_water_assignments.create.step_5.usage_in")}
            </Label>
            <div className="space-y-3">
              {["Irrigation", "Non-irrigation"].map(value => {
                return (
                  <RadioInput
                    key={value}
                    htmlFor="zeroValueTradeReason"
                    label={value}
                    checked={usageIn === value}
                    onChange={() => {
                      setUsageIn(value);
                    }}
                  />
                );
              })}
            </div>
          </div>
        </fieldset>

        <fieldset className="space-y-6">
          <Legend>{t("extraction_right.create.step_2.billing_details")}</Legend>
          {selectedExtractionRights.map(i => (
            <div key={i.id} className="space-y-2">
              <div className="space-y-2">
                <p>
                  {t("extraction_right.name")}: {i.name}
                </p>
                <p>
                  {t("common.single_water_class")}: {i.waterClassName}
                </p>
              </div>
              <div className="flex gap-4">
                <div className="flex-1">
                  <Label htmlFor={`billingGroup--${i.id}`}>
                    {t("extraction_right.create.step_2.billing_group")}
                  </Label>
                  <SelectBillingGroup
                    level1ResourceId={level1Resource.id}
                    value={i.billingGroup?.id}
                    onChange={(e: any) => {
                      if (e) {
                        changeBillingGroup(i.id, {
                          id: e.value,
                          name: e.label,
                        });
                      }
                    }}
                    inputId={`billingGroup--${i.id}`}
                    openMenuOnFocus
                  />
                </div>
              </div>
            </div>
          ))}
        </fieldset>
      </div>

      <footer className="flex gap-4 -mx-6 mt-6 p-6 pb-0 border-t border-gray-200">
        <button type="button" className="btn-outline-primary" onClick={onPrev}>
          {t("common.prev_step")}
        </button>

        <button
          type="submit"
          className="btn-primary"
          disabled={!isAllBillingGroupsSelected}
        >
          {t("common.next_step")}
        </button>

        <button
          type="button"
          className="btn-outline-primary"
          onClick={onCancel}
        >
          {t("common.cancel")}
        </button>
      </footer>
    </form>
  );
};

export default EnterDetails;
